import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import type { APIResponse } from "@/lib/types";
import type {
  CreateDashboardTable,
  DashboardClaimActionBody,
  DashboardClaimActionRead,
  DashboardActionColumn,
  DashboardCellsFilters,
  DashboardCellsRead,
  DashboardDataColumn,
  DashboardPhaseOption,
  DashboardRowsFilters,
  DashboardRowsRead,
  DashboardTableReorderBody,
  DashboardUnassignedActionsFilters,
  DashboardUnassignedActionsRead,
  MatterDashboardTable,
  UpdateDashboardTable,
} from "@/lib/types/MatterDashboardTable";

const matterRoute = "matters/v2";
const tableRoute = `${matterRoute}/dashboard-tables`;
const tabRoute = `${matterRoute}/dashboard-tabs`;
const repeatedParams = { indexes: null } as const;

export const retrieveDashboardPhaseOptions = async (
  params: {
    q?: string;
    pageNumber?: number;
    pageSize?: number;
  } = {},
) =>
  authenticatedFastApiAxiosInstance
    .get<APIResponse<DashboardPhaseOption[]>>(
      `${matterRoute}/dashboard-phase-options`,
      {
        params,
      },
    )
    .then((response) => response.data);

export const retrieveDashboardActionOptions = async (phaseIds: string[]) =>
  authenticatedFastApiAxiosInstance
    .get<APIResponse<DashboardActionColumn[]>>(
      `${matterRoute}/dashboard-action-options`,
      {
        params: { phaseId: phaseIds },
        paramsSerializer: repeatedParams,
      },
    )
    .then((response) => response.data);

export const retrieveDashboardDataItemOptions = async () =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<DashboardDataColumn[]>
    >(`${matterRoute}/dashboard-data-item-options`)
    .then((response) => response.data);

export const retrieveDashboardTables = async (tabId: string) =>
  authenticatedFastApiAxiosInstance
    .get<APIResponse<MatterDashboardTable[]>>(`${tabRoute}/${tabId}/tables`)
    .then((response) => response.data);

export const retrieveDashboardTable = async (tableId: string) =>
  authenticatedFastApiAxiosInstance
    .get<APIResponse<MatterDashboardTable>>(`${tableRoute}/${tableId}`)
    .then((response) => response.data);

export const createDashboardTable = async ({
  tabId,
  body,
}: {
  tabId: string;
  body: CreateDashboardTable;
}) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterDashboardTable>
    >(`${tabRoute}/${tabId}/tables`, body)
    .then((response) => response.data);

export const updateDashboardTable = async ({
  tableId,
  body,
}: {
  tableId: string;
  body: UpdateDashboardTable;
}) =>
  authenticatedFastApiAxiosInstance
    .patch<APIResponse<MatterDashboardTable>>(`${tableRoute}/${tableId}`, body)
    .then((response) => response.data);

export const deleteDashboardTable = async ({
  tableId,
  dateModified,
}: {
  tableId: string;
  dateModified: string;
}) =>
  authenticatedFastApiAxiosInstance
    .delete<APIResponse<MatterDashboardTable>>(`${tableRoute}/${tableId}`, {
      params: { dateModified },
    })
    .then((response) => response.data);

export const reorderDashboardTables = async ({
  tabId,
  body,
}: {
  tabId: string;
  body: DashboardTableReorderBody;
}) =>
  authenticatedFastApiAxiosInstance
    .patch<
      APIResponse<MatterDashboardTable[]>
    >(`${tabRoute}/${tabId}/tables/order`, body)
    .then((response) => response.data);

export const retrieveDashboardRows = async (
  tableId: string,
  filters: DashboardRowsFilters = {},
  signal?: AbortSignal,
) =>
  authenticatedFastApiAxiosInstance
    .get<APIResponse<DashboardRowsRead>>(`${tableRoute}/${tableId}/rows`, {
      params: filters,
      paramsSerializer: repeatedParams,
      signal,
    })
    .then((response) => response.data);

export const retrieveDashboardCells = async (
  tableId: string,
  filters: DashboardCellsFilters,
  signal?: AbortSignal,
) =>
  authenticatedFastApiAxiosInstance
    .get<APIResponse<DashboardCellsRead>>(`${tableRoute}/${tableId}/cells`, {
      params: filters,
      paramsSerializer: repeatedParams,
      signal,
    })
    .then((response) => response.data);

export const retrieveDashboardUnassignedActions = async (
  tableId: string,
  filters: DashboardUnassignedActionsFilters = {},
  signal?: AbortSignal,
) =>
  authenticatedFastApiAxiosInstance
    .get<
      APIResponse<DashboardUnassignedActionsRead>
    >(`${tableRoute}/${tableId}/unassigned-actions`, { params: filters, paramsSerializer: repeatedParams, signal })
    .then((response) => response.data);

export const claimPhaseActionTask = async ({
  phaseActionId,
  body,
}: {
  phaseActionId: string;
  body: DashboardClaimActionBody;
}) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<DashboardClaimActionRead>
    >(`${matterRoute}/phase-actions/${phaseActionId}/claim-task`, body)
    .then((response) => response.data);
